import { useCallback, useState } from "react";
import { Link } from "react-router-dom";

import { CheckButton, GalleryButton, VerifiedTick, ViewsButton } from "components/buttons";
import { formatDuration } from "utils/parseNumber";
import { Image } from "components";
import { className } from "utils/strings";

import lockIcon from "components/icons/lockIcon.svg";

import "./TileItem.scss";
import { metrics, ViewRegion } from "utils/metrics";

export const TileItem = (props: {
  thumbnail: string;
  video: string | undefined;
  published: boolean;
  verified: boolean;
  link: string;
  views: number;
  duration: number;
  gallery: boolean;

  manage: boolean;
  checked?: boolean;
  onCheck?: (checked: boolean) => void;

  index: number;
  source: ViewRegion;
}) => {
  const [isHover, setHover] = useState<boolean>(false);
  const [isVideoAvailable, setVideoAvailable] = useState<boolean>(false);

  const onCheck = (): void => {
    if (props.onCheck) {
      props.onCheck(!props.checked);
    }
  };

  /**
   * Prevent link following in manage mode,
   * we just need to check / uncheck the items.
   */
  const onLinkClick = async (e: any) => {
    if (props.manage) {
      e.preventDefault();
      onCheck();
    }
    await metrics.sendGifClick({
      position: props.index,
      source: props.source,
    });
  };

  const topLeftCorner = () => {
    if (props.manage) {
      return (
        <CheckButton
          checked={!!props.checked}
          onClick={onCheck}
        />
      );
    }

    if (props.verified) {
      return (
        <VerifiedTick
          hint="Gif from a verified creator"
        />
      );
    }

    if (!props.published) {
      return (
        <div className="private" title="This gif is private.">
          <img src={lockIcon} className="lockIcon" alt="" />
        </div>
      );
    }
  };

  const topRightCorner = () => {
    if (props.gallery) {
      return (
        <div className="duration">
          <GalleryButton />
        </div>
      );
    }

    if (props.video && props.duration > 0) {
      return (
        <div className="duration">
          {formatDuration(props.duration)}
        </div>
      );
    }

    if (props.video && props.views > 0) {
      return (
        <div className="duration">
          {formatDuration(props.duration)}
        </div>
      );
    }
  };

  const bottomLeftCorner = () => {
    if (props.views > 0 && props.video) {
      return (
        <ViewsButton
          count={props.views}
          type={1}
        />
      );
    }

    if (props.views > 0 && !props.video) {
      return (
        <ViewsButton
          count={props.views}
          type={2}
        />
      );
    }
  };

  const handleMouseEnter = (e: any) => {
    setHover(true);
  };

  const handleMouseLeave = () => {
    setHover(false);
    setVideoAvailable(false);
  };

  const handleLoadedData = useCallback(() => {
    setVideoAvailable(true);

    console.debug("The video is available.");
  }, []);

  return (
    <Link to={props.link} className={className({
      "tile": true,
      "isSelected": !!props.checked,
    })} style={{
      aspectRatio: "50/50",
    }} onClick={onLinkClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Image
        className="thumbnail"
        src={props.thumbnail}
      />

      {isHover && props.video && (
        <video
          className={className({
            "loading": !isVideoAvailable,
            "ready": isVideoAvailable,
          })}
          controls={false}
          autoPlay={true}
          loop={true}
          playsInline
          preload="auto"
          muted={true}
          onLoadedData={handleLoadedData}
        >
          <source src={props.video} type="video/mp4" />
        </video>
      )}

      <div className="shadow"></div>
      <div className="shadow-hover"></div>

      {topLeftCorner()}
      {topRightCorner()}
      {bottomLeftCorner()}
    </Link>
  );
};
